import { useEffect, useState, useRef } from "react";
import { Linking } from "react-native";
import { os, isMobileOrMobileWeb, agent } from "../platform";
import AppStore from "../storage/appStore";
import { STRINGS } from "../constants/strings";
import { COLORS } from "../theme/colors";
import { logError } from "../platform/platform.web";
import { fontFamilyStyle } from "../utils/styles/typography";

/**
 * InstallPromptWeb component displays a prompt to install the web app as a PWA or redirects to app stores on mobile devices.
 *
 * - Handles the `beforeinstallprompt` event for PWA installation on supported browsers.
 * - Shows a custom install banner for mobile and desktop users.
 * - Redirects mobile users to the appropriate app store if on iOS or Android.
 * - Allows users to dismiss the install prompt, suppressing it for the session.
 *
 * @component
 * @returns {JSX.Element|null} The install prompt banner, or null if not shown.
 */
const InstallPromptWeb = () => {
  const webInstaller = AppStore((state) => state.webInstaller);
  const setWebInstaller = AppStore((state) => state.setWebInstaller);
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  const isMobile = isMobileOrMobileWeb();
  const styles = getStyles(isMobile);
  const userAgent = agent();
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

    if (os !== "web" || isStandalone || webInstaller.installed || webInstaller.installerDismissed) {
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    const fallbackTimeout = setTimeout(() => {
      if (!deferredPromptRef.current && isMobile && !webInstaller.sessionSuppress) {
        setShowPrompt(true);
        setWebInstaller({
          promptWasShown: true,
          sessionSuppress: true,
        });
      }
    }, 8000);

    return () => {
      clearTimeout(fallbackTimeout);
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, [
    setWebInstaller,
    webInstaller.installed,
    webInstaller.installerDismissed,
    webInstaller.sessionSuppress,
    isMobile,
  ]);

  const handleClick = async () => {
    if (isMobile) {
      try {
        if (userAgent === "ios") {
          await Linking.openURL(STRINGS.appleAppstoreLink);
        } else if (userAgent === "android") {
          await Linking.openURL(STRINGS.googlePlaystoreLink);
        } else {
          setErrorMessage("Your device is not supported for direct installation.");
        }
      } catch (error) {
        logError("app_store_link_failed", {
          message: error.message,
          userAgent,
          url: userAgent === "ios" ? STRINGS.appleAppstoreLink : STRINGS.googlePlaystoreLink,
        });
        setErrorMessage(STRINGS.failedToOpenLink);
      }
    } else {
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
          setWebInstaller({ installed: true });
          console.log("App installed!");
        } else {
          console.log("User dismissed install");
        }
        setDeferredPrompt(null);
      } else {
        setErrorMessage("To install, tap the browser menu and select 'Add to Home screen'");
      }
    }
  };

  if (!showPrompt) return null;

  const label = isMobile ? "Install App" : "Add to Home Screen";

  return (
    <div style={styles.banner}>
      <img src="/assets/icons/icon-96.webp" alt="VB Connect" style={styles.icon} />
      <div style={styles.text}>
        <div style={styles.title}>VB Connect</div>
      </div>
      <button style={styles.button} onClick={handleClick}>
        {label}
      </button>
      <button
        style={styles.close}
        aria-label="Close desktop installer prompt"
        onClick={() => {
          setShowPrompt(false);
          setWebInstaller({ installerDismissed: true });
          setDeferredPrompt(null);
        }}
      >
        ×
      </button>
    </div>
  );
};

const getStyles = (isMobile) => ({
  banner: {
    position: "fixed",
    bottom: 70,
    right: 16,
    backgroundColor: COLORS.primaryVariant,
    border: `1px solid ${COLORS.secondary}`,
    borderRadius: 12,
    padding: isMobile ? "12px 16px" : "14px 18px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    zIndex: 9999,
    maxWidth: isMobile ? "90%" : 460,
  },
  icon: {
    width: isMobile ? 48 : 40,
    height: isMobile ? 48 : 40,
    borderRadius: 8,
    marginRight: 12,
  },
  text: {
    flex: 1,
    fontSize: 14,
    lineHeight: "1.2em",
  },
  title: {
    fontWeight: "bold",
    color: COLORS.text,
    ...fontFamilyStyle,
  },
  button: {
    backgroundColor: COLORS.primary,
    color: "white",
    border: "none",
    padding: isMobile ? "8px 14px" : "8px 14px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: "bold",
    marginLeft: 12,
    cursor: "pointer",
  },
  close: {
    background: "none",
    border: "none",
    fontSize: 18,
    marginLeft: 10,
    cursor: "pointer",
    color: COLORS.textSecondary,
  },
});

export default InstallPromptWeb;
